/**
 * Hero mask geometry — the single source of truth for both the clip mask and its
 * border outline, and the only place the two shapes are defined.
 *
 * Each shape is one closed path of 12 cubic segments in objectBoundingBox space
 * (0→1 fractions of the media box), stored as a flat array:
 *   [ startX, startY,  (c1x,c1y, c2x,c2y, endX,endY) × 12 ]
 *
 * The two shapes are authored with *identical topology* so `d` interpolates
 * anchor-for-anchor:
 *   - The desktop pill's two straight edges (originally `H` commands) are written
 *     as degenerate cubics with colinear controls, so both shapes are all-cubic.
 *   - The mobile blob is rotated/re-sequenced to start at the anchor matching the
 *     desktop start and to wind the same direction, so anchor i → anchor i maps
 *     corner→corner and edge-pinch→edge-pinch. Without this the morph twists.
 */

// prettier-ignore
const DESKTOP_POINTS = [
  0.995, 0.7475,
  0.995, 0.6634, 0.9722, 0.6079, 0.9445, 0.5713, // up the right side
  0.9168, 0.5347, 0.9178, 0.4703, 0.9445, 0.4317,
  0.9752, 0.3871, 0.995, 0.3228, 0.995, 0.2525,
  0.995, 0.1159, 0.9227, 0.005, 0.8326, 0.005,   // top-right corner
  0.6132, 0.005, 0.3937, 0.005, 0.1743, 0.005,   // flat top edge (was H)
  0.0852, 0.005, 0.008, 0.109, 0.005, 0.2446,    // top-left corner
  0.004, 0.3178, 0.0228, 0.3842, 0.0555, 0.4317, // down the left side
  0.0812, 0.4693, 0.0812, 0.5307, 0.0555, 0.5683,
  0.0228, 0.6158, 0.004, 0.6822, 0.005, 0.7554,
  0.008, 0.8911, 0.0852, 0.995, 0.1743, 0.995,   // bottom-left corner
  0.3937, 0.995, 0.6132, 0.995, 0.8326, 0.995,   // flat bottom edge (was H)
  0.9227, 0.995, 0.995, 0.8841, 0.995, 0.7475,   // bottom-right corner, close
];

// prettier-ignore
const MOBILE_POINTS = [
  0.8915, 0.8911,                                 // start aligned to desktop P0
  0.9931, 0.7892, 1.0, 0.6286, 0.9127, 0.5184,   // up the right side
  0.9041, 0.5076, 0.9041, 0.4925, 0.9127, 0.4817, // right-edge scallop
  1.0, 0.3715, 0.9931, 0.2108, 0.8915, 0.109,    // top-right corner
  0.7894, 0.0065, 0.6278, 0.0, 0.5179, 0.0891,   // across the top
  0.5072, 0.0978, 0.4919, 0.0979, 0.481, 0.0892, // top-edge scallop
  0.3711, 0.0011, 0.2104, 0.0079, 0.1086, 0.11,  // top-left corner
  0.007, 0.2119, 0.0, 0.3727, 0.0874, 0.4828,    // down the left side
  0.0959, 0.4936, 0.096, 0.5087, 0.0875, 0.5195, // left-edge scallop
  0.0012, 0.6297, 0.0084, 0.7895, 0.1097, 0.8911, // bottom-left corner
  0.2116, 0.9933, 0.3723, 1.0, 0.4822, 0.9118,   // across the bottom
  0.4929, 0.9032, 0.5082, 0.9032, 0.519, 0.9118, // bottom-edge scallop
  0.6289, 1.0, 0.7896, 0.9932, 0.8915, 0.8911,   // bottom-right corner, close
];

// Per-shape box + stroke, mirroring the resting values in _hero-home.scss so the
// morph endpoints line up exactly with the static CSS at the handoff.
export const SHAPES = {
  desktop: { points: DESKTOP_POINTS, aspectRatio: 761 / 500, maxWidth: '760px', stroke: 12 },
  mobile:  { points: MOBILE_POINTS,  aspectRatio: 380 / 381, maxWidth: '30rem', stroke: 8 },
};

/** Build an SVG path string from a flat points array (M + 12×C + Z). */
export function buildPath(p) {
  let d = `M${p[0]} ${p[1]}`;
  for (let i = 2; i < p.length; i += 6) {
    d += `C${p[i]} ${p[i + 1]} ${p[i + 2]} ${p[i + 3]} ${p[i + 4]} ${p[i + 5]}`;
  }
  return `${d}Z`;
}

/** Element-wise linear interpolation of two equal-length points arrays. */
export function lerpPoints(a, b, t) {
  const out = new Array(a.length);
  for (let i = 0; i < a.length; i++) out[i] = a[i] + (b[i] - a[i]) * t;
  return out;
}
